// src/core/data-tables/useFilters.tsx
import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import {
  OPS,
  OpKey,
  Filter,
  buildWhereClause,
} from "./filterUtils";

/*----------------------------------------------------------------
  Hook: CRUD for filters + WHERE clause + global AND/OR selector
----------------------------------------------------------------*/
export function useFilters(columns: string[]) {
  const nextId = useRef(1);
  const [filters, setFilters] = useState<Filter[]>([]);
  const [applied, setApplied] = useState<Filter[]>([]);
  const [joinOp, setJoinOp] = useState<"AND" | "OR">("AND");   // NEW

  /* prune filters when column set changes ---------------------- */
  useEffect(() => {
    setFilters((f) =>
      f.filter(({ column }) => !column || columns.includes(column)),
    );
    setApplied((a) =>
      a.filter(({ column }) => !column || columns.includes(column)),
    );
  }, [columns]);

  /* dirty? ----------------------------------------------------- */
  const dirty = useMemo(
    () =>
      JSON.stringify(filters) !== JSON.stringify(applied) ||
      filters.length === 0 && applied.length !== 0,
    [filters, applied],
  );

  /* CRUD helpers ---------------------------------------------- */
  const addFilter = useCallback(
    () =>
      setFilters((f) => [
        ...f,
        { id: nextId.current++, column: "", op: "=", value: "" },
      ]),
    [],
  );

  const updateFilter = useCallback(
    (id: number, patch: Partial<Filter>) =>
      setFilters((f) => f.map((r) => (r.id === id ? { ...r, ...patch } : r))),
    [],
  );

  const removeFilter = useCallback(
    (id: number) => setFilters((f) => f.filter((r) => r.id !== id)),
    [],
  );

  const clearFilters = useCallback(() => {
    setFilters([]);
    setApplied([]);
  }, []);

  const applyFilters = useCallback(() => setApplied(filters), [filters]);

  /* WHERE clause ---------------------------------------------- */
  const whereClause = useMemo(
    () => buildWhereClause(applied, joinOp),
    [applied, joinOp],
  );

  return {
    filters,
    applied,
    dirty,
    joinOp,
    setJoinOp,
    addFilter,
    updateFilter,
    removeFilter,
    clearFilters,
    applyFilters,
    whereClause,
    /* pass through for UI */
    OPS,
  };
}

/* re-export */
export { OPS };
export type { Filter, OpKey };
